import cx from "classnames";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { createPortal } from "react-dom";

import type { DisplayLoyaltyLadderStep } from "@/api/orval/model";
import { useTranslation } from "@/core/i18n";
import { DraggableModal } from "atoms/draggable-modal/DraggableModal";
import { Button } from "atoms/button/Button";

import { LoyaltyTierBadge } from "./LoyaltyTierBadge";
import type { EnabledLoyaltyStatus } from "./useLoyaltyStatus";

type HowItWorksSheetProps = {
  open: boolean;
  status: EnabledLoyaltyStatus;
  onClose: () => void;
};

type Translate = ReturnType<typeof useTranslation>["t"];

/** "3 visits", or "From day one" for the base tier every client starts on. */
const qualifiesAtCopy = (t: Translate, tier: DisplayLoyaltyLadderStep["tier"]) =>
  tier.qualifyAtVisits === 0
    ? t("loyalty.qualifyFromJoining")
    : t("loyalty.qualifyVisits", { count: tier.qualifyAtVisits });

const rewardTitle = (reward: DisplayLoyaltyLadderStep["tierRewards"][number]) => {
  if (!reward.title) {
    throw new Error(`Tier reward ${reward.rewardId} has no title`);
  }
  return reward.title;
};

type TierRowProps = {
  readonly step: DisplayLoyaltyLadderStep;
  readonly isCurrentTier: boolean;
};

/** One tier: its name and threshold always showing, what it gives in a section that opens and closes. */
const TierRow = ({ step: { tier, tierRewards }, isCurrentTier }: TierRowProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(isCurrentTier);
  const panelId = useId();

  return (
    <li aria-label={tier.name} className="py-1">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((open) => !open)}
        className="flex w-full items-center gap-3 py-3 text-left"
      >
        <LoyaltyTierBadge tier={tier} size="md" />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <span className="text-body1">{tier.name}</span>
            {isCurrentTier && (
              <span className="rounded-full bg-background-two px-2 py-0.5 text-body3 text-secondary">
                {t("loyalty.youAreHere")}
              </span>
            )}
          </span>
          <span className="text-body2 text-secondary">{qualifiesAtCopy(t, tier)}</span>
        </span>
        <ChevronDown
          aria-hidden
          className={cx("h-5 w-5 shrink-0 text-secondary transition-transform", {
            "rotate-180": expanded,
          })}
        />
      </button>
      {expanded && (
        <div id={panelId} className="flex flex-col gap-2 pb-3 pl-[calc(theme(spacing.3)+2.5rem)]">
          {tier.membersOnlyDescription && (
            <p className="whitespace-pre-line text-body2 text-primary">
              {tier.membersOnlyDescription}
            </p>
          )}
          {tier.description && <p className="text-body2 text-primary">{tier.description}</p>}
          {tierRewards.length > 0 ? (
            <ul
              aria-label={t("loyalty.tierRewardsOf", { tier: tier.name })}
              className="flex list-disc flex-col gap-1 pl-5 text-body2 text-primary"
            >
              {tierRewards.map((reward) => (
                <li key={reward.rewardId}>{rewardTitle(reward)}</li>
              ))}
            </ul>
          ) : (
            !tier.description &&
            !tier.membersOnlyDescription && (
              <p className="text-body2 text-secondary">{t("loyalty.benefitsComingSoon")}</p>
            )
          )}
        </div>
      )}
    </li>
  );
};

/**
 * The program explained: every tier on the client's ladder (their home clinic's) with its threshold
 * always showing and, in a collapsible section, what it gives. The current tier starts open.
 * Portalled to the body so the header popover cannot trap the fixed sheet, inside its own `app`
 * query container (as the popover has) so `desk:` styles still make it a centred dialog, stacked
 * above the popover. The body scrolls inside the sheet when the list outgrows the screen.
 */
export const HowItWorksSheet = ({ open, status, onClose }: HowItWorksSheetProps) => {
  const { t } = useTranslation();

  return createPortal(
    <div
      style={{ containerType: "inline-size", containerName: "app" }}
      className="pointer-events-none fixed inset-0 z-[60] [&>*:first-child]:pointer-events-auto"
    >
      <DraggableModal
        open={open}
        onClose={onClose}
        header={
          <h2 className="flex w-full items-center justify-center border-b border-light-grey pb-5 pt-6 font-sans text-bold2 uppercase tracking-[.04em] text-primary">
            {t("loyalty.howItWorksTitle")}
          </h2>
        }
        footer={
          <div className="px-5 py-4">
            <Button fullWidth onClick={onClose}>
              {t("loyalty.gotIt")}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-2 px-5 pb-2 text-left text-primary">
          {status.currentTier && (
            <div className="flex flex-col items-center gap-2 pt-5">
              <LoyaltyTierBadge tier={status.currentTier} size="lg" />
              <span className="text-body1 font-semibold">{status.currentTier.name}</span>
            </div>
          )}
          <ul
            aria-label={t("loyalty.tiersTitle")}
            className="flex flex-col divide-y divide-light-grey"
          >
            {status.ladder.map((step) => (
              <TierRow
                key={step.tier.id}
                step={step}
                isCurrentTier={status.currentTier?.id === step.tier.id}
              />
            ))}
          </ul>
        </div>
      </DraggableModal>
    </div>,
    document.body,
  );
};
